import React from 'react';
import { X, Key, Workflow } from 'lucide-react';
import { ApiKeyTabView } from './settings/ApiKeyTab';
import { ComfyUITab } from './settings/ComfyUITab';
import { useApiKeyManager } from '../hooks/useApiKeyManager';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 弹窗层级：默认 100（编辑器内）；在首页顶栏（z-200）打开时需传入更高层级以覆盖顶栏 */
  zIndex?: number;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, zIndex = 100 }) => {
  const [activeTab, setActiveTab] = React.useState<'keys' | 'comfyui'>('keys');

  const {
    polloKey, setPolloKey,
    runningHubKey, setRunningHubKey,
    grsaiKey, setGrsaiKey,
    falKey, setFalKey,
    agnesKey, setAgnesKey,
    wavespeedKey, setWavespeedKey,
    replicateKey, setReplicateKey,
    savedMasked,
    isSaved,
    saveError,
    handleSave,
  } = useApiKeyManager(isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200" style={{ zIndex }} onClick={onClose}>
      <div
        className="w-[560px] max-h-[80vh] bg-[#1c1c1e] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-white/5 flex justify-between items-center bg-white/5">
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X size={18} />
          </button>
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-slate-700/50 rounded-lg">
              <Key size={16} className="text-white" />
            </div>
            <span className="text-sm font-bold text-white">设置 (Settings)</span>
          </div>
        </div>

        <div className="flex border-b border-white/5">
          <button
            onClick={() => setActiveTab('keys')}
            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === 'keys' ? 'text-cyan-400 bg-white/5' : 'text-slate-500 hover:text-white'}`}
          >
            API Keys
          </button>
          <button
            onClick={() => setActiveTab('comfyui')}
            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-2 ${activeTab === 'comfyui' ? 'text-cyan-400 bg-white/5' : 'text-slate-500 hover:text-white'}`}
          >
            <Workflow size={14} />
            ComfyUI 工作流
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {activeTab === 'keys' ? (
            <ApiKeyTabView
              polloKey={polloKey} setPolloKey={setPolloKey}
              runningHubKey={runningHubKey} setRunningHubKey={setRunningHubKey}
              grsaiKey={grsaiKey} setGrsaiKey={setGrsaiKey}
              falKey={falKey} setFalKey={setFalKey}
              agnesKey={agnesKey} setAgnesKey={setAgnesKey}
              wavespeedKey={wavespeedKey} setWavespeedKey={setWavespeedKey}
              replicateKey={replicateKey} setReplicateKey={setReplicateKey}
              savedMasked={savedMasked}
            />
          ) : (
            <ComfyUITab />
          )}
        </div>

        <div className="p-4 border-t border-white/5 bg-[#121214] flex items-center justify-end gap-3">
          {saveError && (
            <span className="mr-auto text-xs text-red-400 truncate max-w-[360px]" title={saveError}>{saveError}</span>
          )}
          <button
            onClick={handleSave}
            className={`px-6 py-2 rounded-xl text-xs font-bold transition-all ${isSaved ? 'bg-green-500 text-white' : 'bg-white text-black hover:bg-cyan-400'}`}
          >
            {isSaved ? '已保存' : '保存设置'}
          </button>
        </div>
      </div>
    </div>
  );
};
